import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ArrowLeft, Clock, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalDraftBanner from '../../components/LegalDraftBanner';

// =============================================================================
// LEGAL DRAFT — REQUIRES AVOCAT REVIEW BEFORE B2C PUBLIC LAUNCH
// =============================================================================
// The Refund Policy content was scaffolded by an earlier session and MUST be
// reviewed and finalized by an avocat (specialised in droit de la consommation
// and droit du transport) before public launch. Known gaps to close:
//   - Interaction with Code de la consommation Art L. 121-21 (cooling-off /
//     droit de rétractation for B2C). For VTC services the standard 14-day
//     withdrawal right does not apply (transport on a specific date is
//     excluded under Art L. 221-28-12°), but this exclusion needs to be
//     stated explicitly and verified for the booking flows we actually offer.
//   - Precise refund timeframes: where the consumer cancels and is owed a
//     refund, Code de la consommation Art L. 216-2 requires reimbursement
//     within 14 days. The current "5 to 10 business days" wording must be
//     reconciled with that statutory ceiling.
//   - Enforceability of the "10 min wait → no-show, 100% billed" timer under
//     French consumer law: must be reviewed against the case law on
//     pre-formulated penalty clauses (clauses pénales) and the abusive-clause
//     control of Art L. 212-1 Code de la consommation.
//   - Late-cancellation 50% fee and "after chauffeur arrives = 100% billed"
//     must be checked against the same abusive-clause framework — proportion
//     to actual prejudice suffered by the operator.
//   - Coordination with the corresponding clause in CGU Art 4 (booking and
//     payment) so a single source of truth governs cancellation terms.
//
// FR is authoritative; non-FR versions are courtesy translations marked via
// LegalDraftBanner.
// =============================================================================

const RefundPolicyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="refund-policy-page">
          <Seo {...seo.refundPolicy} />
      <LegalDraftBanner />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              {t('legalPages.common.backToHome')}
            </Link>

            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {t('legalPages.refundPolicy.title')}
                </h1>
                <p className="text-neutral-500 text-sm mt-1">{t('legalPages.common.lastUpdated')}</p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-12">
              <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <h3 className="font-heading font-semibold text-green-400">{t('legalPages.refundPolicy.summary.freeTitle')}</h3>
                </div>
                <p className="text-neutral-400 text-sm">{t('legalPages.refundPolicy.summary.freeText')}</p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  <h3 className="font-heading font-semibold text-yellow-400">{t('legalPages.refundPolicy.summary.lateTitle')}</h3>
                </div>
                <p className="text-neutral-400 text-sm">{t('legalPages.refundPolicy.summary.lateText')}</p>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-orange-400" />
                  <h3 className="font-heading font-semibold text-orange-400">{t('legalPages.refundPolicy.summary.arrivedTitle')}</h3>
                </div>
                <p className="text-neutral-400 text-sm">{t('legalPages.refundPolicy.summary.arrivedText')}</p>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <h3 className="font-heading font-semibold text-red-400">{t('legalPages.refundPolicy.summary.noShowTitle')}</h3>
                </div>
                <p className="text-neutral-400 text-sm">{t('legalPages.refundPolicy.summary.noShowText')}</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-10">
              {/* Article 1 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.refundPolicy.art1.title')}</h2>
                {[1, 2, 3, 4].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.refundPolicy.art1.sub1${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.refundPolicy.art1.sub1${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Article 2 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.refundPolicy.art2.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.refundPolicy.art2.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3].map(i => <li key={i}>{t(`legalPages.refundPolicy.art2.list${i}`)}</li>)}
                </ul>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.refundPolicy.art2.outro')}</p>
              </section>

              {/* Article 3 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.refundPolicy.art3.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.refundPolicy.art3.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3].map(i => <li key={i}>{t(`legalPages.refundPolicy.art3.list${i}`)}</li>)}
                </ul>
              </section>

              {/* Article 4 */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.refundPolicy.art4.title')}</h2>
                {[1, 2, 3, 4].map(i => (
                  <React.Fragment key={i}>
                    <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">{t(`legalPages.refundPolicy.art4.sub4${i}Title`)}</h3>
                    <p className="text-neutral-400 leading-relaxed">{t(`legalPages.refundPolicy.art4.sub4${i}Body`)}</p>
                  </React.Fragment>
                ))}
              </section>

              {/* Tableau récapitulatif */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-6">{t('legalPages.refundPolicy.art5.title')}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-white font-heading font-semibold py-3 pr-4">{t('legalPages.refundPolicy.art5.colMoment')}</th>
                        <th className="text-white font-heading font-semibold py-3 pr-4">{t('legalPages.refundPolicy.art5.colFees')}</th>
                        <th className="text-white font-heading font-semibold py-3">{t('legalPages.refundPolicy.art5.colRefund')}</th>
                      </tr>
                    </thead>
                    <tbody className="text-neutral-400">
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4">{t('legalPages.refundPolicy.art5.row1Moment')}</td>
                        <td className="py-3 pr-4 text-green-400">0%</td>
                        <td className="py-3 text-green-400">100%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4">{t('legalPages.refundPolicy.art5.row2Moment')}</td>
                        <td className="py-3 pr-4 text-yellow-400">50%</td>
                        <td className="py-3 text-yellow-400">50%</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4">{t('legalPages.refundPolicy.art5.row3Moment')}</td>
                        <td className="py-3 pr-4 text-orange-400">100%</td>
                        <td className="py-3 text-orange-400">0%</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4">{t('legalPages.refundPolicy.art5.row4Moment')}</td>
                        <td className="py-3 pr-4 text-red-400">100%</td>
                        <td className="py-3 text-red-400">0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Contact */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">{t('legalPages.refundPolicy.art6.title')}</h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.refundPolicy.art6.intro')}</p>
                <div className="mt-4 text-neutral-400 space-y-1">
                  <p>Téléphone : 09 39 24 23 20</p>
                  <p>WhatsApp : 07 66 30 15 03</p>
                  <p>Email : contact@blackmatte.fr</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
